import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { PageHeader } from '../components/PageHeader.js';
import { api, type ShareRow } from '../lib/api.js';

function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusStyle(status: string): React.CSSProperties {
  const c =
    status === 'active' ? 'var(--accent-green)' : status === 'revoked' ? 'var(--accent-red)' : 'var(--accent-yellow)';
  return { background: `${c}1f`, color: c, border: `1px solid ${c}55` };
}

export function SharesPage() {
  usePageTitle('Shares');
  const [showCreate, setShowCreate] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['shares'], queryFn: () => api.listShares(), refetchInterval: 15_000 });

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PageHeader
        eyebrow="Workspace"
        title="Shares"
        actions={
          <button
            type="button"
            onClick={() => setShowCreate((s) => !s)}
            className="rounded-lg px-3 py-1.5 text-xs font-semibold"
            style={{ background: 'var(--accent-blue)1f', color: 'var(--accent-blue)', border: '1px solid var(--accent-blue)55' }}
          >
            {showCreate ? 'Close' : '+ New share'}
          </button>
        }
      />
      <div className="flex-1 overflow-y-auto p-6 space-y-6 max-w-5xl">
        <p className="text-[13px] text-text-secondary leading-snug max-w-2xl">
          A share is a secure link you send to a client so they can drop files for an upcoming import. Files land in the
          build's namespace in S3. Lock a share to the client's email to also let them see what's already been uploaded.
        </p>

        {showCreate && <CreateShare onDone={() => setShowCreate(false)} />}

        {isLoading && <div className="text-sm text-text-dimmed">Loading…</div>}
        {data && data.shares.length === 0 && !showCreate && (
          <div className="glass rounded-2xl p-8 text-center text-text-dimmed text-sm">No shares yet. Create one to get a client upload link.</div>
        )}

        {data && data.shares.length > 0 && (
          <div className="glass rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-text-dimmed" style={{ borderBottom: '1px solid var(--border)' }}>
                  <th className="px-4 py-2 text-left font-semibold">Share</th>
                  <th className="px-3 py-2 text-left font-semibold">Build</th>
                  <th className="px-3 py-2 text-left font-semibold">Lock</th>
                  <th className="px-3 py-2 text-right font-semibold">Drops</th>
                  <th className="px-3 py-2 text-left font-semibold">Status</th>
                  <th className="px-3 py-2 text-left font-semibold">Expires</th>
                  <th className="px-3 py-2 text-right font-semibold">Link</th>
                </tr>
              </thead>
              <tbody>
                {data.shares.map((s) => (
                  <ShareRowView key={s.id} share={s} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ShareRowView({ share }: { share: ShareRow }) {
  const portalUrl = share.portalUrl ?? (share.token ? `${window.location.origin}/portal.html?token=${share.token}` : undefined);
  return (
    <tr style={{ borderBottom: '1px solid var(--border)' }}>
      <td className="px-4 py-2">
        <Link to="/shares/$id" params={{ id: share.id }} className="text-text-primary hover:underline font-medium">
          {share.label || 'Untitled share'}
        </Link>
        <div className="text-[10px] text-text-dimmed">{ago(share.createdAt)} · {share.createdBy}</div>
      </td>
      <td className="px-3 py-2 text-text-secondary">{share.buildDisplayName ?? share.namespace}</td>
      <td className="px-3 py-2 text-[12px]">
        {share.lockedEmail ? <span title={share.lockedEmail} style={{ color: 'var(--accent-blue)' }}>🔒 email</span> : <span className="text-text-dimmed">open</span>}
      </td>
      <td className="px-3 py-2 text-right font-mono">{share.dropCount ?? 0}</td>
      <td className="px-3 py-2"><span className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase" style={statusStyle(share.status)}>{share.status}</span></td>
      <td className="px-3 py-2 text-[12px] text-text-secondary">{ago(share.expiresAt) === '—' ? 'never' : ago(share.expiresAt)}</td>
      <td className="px-3 py-2 text-right">
        {portalUrl && (
          <button
            type="button"
            onClick={() => { void navigator.clipboard.writeText(portalUrl); toast.success('Link copied'); }}
            className="rounded-lg px-2 py-1 text-[11px] font-semibold"
            style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}
          >
            Copy link
          </button>
        )}
      </td>
    </tr>
  );
}

const EXPIRY_OPTIONS = [
  { label: '30 days', days: 30 },
  { label: '7 days', days: 7 },
  { label: '90 days', days: 90 },
  { label: 'Never', days: 0 },
] as const;

function CreateShare({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [buildId, setBuildId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [label, setLabel] = useState('');
  const [lockedEmail, setLockedEmail] = useState('');
  const [expiryDays, setExpiryDays] = useState(30);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  const builds = useQuery({ queryKey: ['builds', 'all'], queryFn: () => api.listBuilds() });
  const sessions = useQuery({ queryKey: ['sessions', buildId], queryFn: () => api.listSessions(buildId), enabled: !!buildId });

  const create = useMutation({
    mutationFn: () =>
      api.createShare({
        buildId,
        sessionId: sessionId || undefined,
        label: label.trim() || undefined,
        lockedEmail: lockedEmail.trim() || undefined,
        expiresAt: expiryDays === 0 ? null : new Date(Date.now() + expiryDays * 86400_000).toISOString(),
      }),
    onSuccess: (res) => {
      const url = res.share.portalUrl ?? (res.share.token ? `${window.location.origin}/portal.html?token=${res.share.token}` : null);
      setCreatedUrl(url);
      void qc.invalidateQueries({ queryKey: ['shares'] });
      toast.success('Share created');
    },
    onError: (e) => toast.error(`Create failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  const field = 'w-full rounded-lg px-3 py-2 text-sm';
  const fieldStyle: React.CSSProperties = { background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' };

  if (createdUrl) {
    return (
      <div className="glass rounded-2xl p-5 space-y-3" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)' }}>
        <div className="text-sm font-semibold text-text-primary">Share link ready</div>
        <div className="flex gap-2 items-center">
          <input readOnly value={createdUrl} className={field} style={fieldStyle} onFocus={(e) => e.currentTarget.select()} />
          <button type="button" onClick={() => { void navigator.clipboard.writeText(createdUrl); toast.success('Copied'); }}
            className="rounded-lg px-3 py-2 text-xs font-semibold whitespace-nowrap" style={{ background: 'var(--accent-blue)1f', color: 'var(--accent-blue)', border: '1px solid var(--accent-blue)55' }}>
            Copy
          </button>
        </div>
        <p className="text-[12px] text-text-dimmed">Send this to your client. {lockedEmail ? 'They must verify their email before they can list files.' : 'Anyone with the link can drop files (they cannot see existing files).'}</p>
        <button type="button" onClick={onDone} className="text-xs text-text-secondary hover:underline">Done</button>
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl p-5 space-y-4" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.25)' }}>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <label className="space-y-1">
          <div className="text-[11px] uppercase tracking-widest text-text-dimmed">Build (required)</div>
          <select className={field} style={fieldStyle} value={buildId} onChange={(e) => { setBuildId(e.target.value); setSessionId(''); }}>
            <option value="">Select a build…</option>
            {builds.data?.builds.map((b) => <option key={b.id} value={b.id}>{b.displayName} ({b.region})</option>)}
          </select>
        </label>
        <label className="space-y-1">
          <div className="text-[11px] uppercase tracking-widest text-text-dimmed">Import session (optional)</div>
          <select className={field} style={fieldStyle} value={sessionId} onChange={(e) => setSessionId(e.target.value)} disabled={!buildId}>
            <option value="">None</option>
            {sessions.data?.sessions.map((s) => <option key={s.id} value={s.id}>{s.title || s.id.slice(0, 8)} · {s.status}</option>)}
          </select>
        </label>
        <label className="space-y-1">
          <div className="text-[11px] uppercase tracking-widest text-text-dimmed">Label (optional)</div>
          <input className={field} style={fieldStyle} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Q3 migration drop" />
        </label>
        <label className="space-y-1">
          <div className="text-[11px] uppercase tracking-widest text-text-dimmed">Lock to email (optional)</div>
          <input className={field} style={fieldStyle} value={lockedEmail} onChange={(e) => setLockedEmail(e.target.value)} placeholder="client@practice.com" />
        </label>
        <label className="space-y-1">
          <div className="text-[11px] uppercase tracking-widest text-text-dimmed">Expires</div>
          <select className={field} style={fieldStyle} value={expiryDays} onChange={(e) => setExpiryDays(Number(e.target.value))}>
            {EXPIRY_OPTIONS.map((o) => <option key={o.label} value={o.days}>{o.label}</option>)}
          </select>
        </label>
      </div>
      {!lockedEmail && (
        <div className="text-[12px]" style={{ color: 'var(--accent-yellow)' }}>
          ⚠ No email lock — anyone with the link can upload. For PHI, prefer locking to the client's email.
        </div>
      )}
      <div className="flex gap-2">
        <button type="button" disabled={!buildId || create.isPending} onClick={() => create.mutate()}
          className="rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-40"
          style={{ background: 'var(--accent-blue)1f', color: 'var(--accent-blue)', border: '1px solid var(--accent-blue)55' }}>
          {create.isPending ? 'Creating…' : 'Create share'}
        </button>
        <button type="button" onClick={onDone} className="rounded-lg px-4 py-2 text-sm text-text-secondary">Cancel</button>
      </div>
    </div>
  );
}
